import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Plus, Briefcase, MapPin, ExternalLink, Trash2, Edit3,
  Check, X, Search, Calendar, Shield, FileText, Link as LinkIcon,
  ChevronDown, Filter, Mail, Eye, TrendingRight, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import CelebrateModal from "../components/CelebrateModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "saved", label: "Saved", color: "#64748b", bg: "rgba(100,116,139,0.12)" },
  { value: "applied", label: "Applied", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  { value: "shortlisted", label: "Shortlisted", color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  { value: "interviewed", label: "Interviewed", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { value: "offer_received", label: "Offer Received", color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  { value: "rejected", label: "Rejected", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  { value: "rtr_submitted", label: "RTR Submitted", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
];

const statusMap = Object.fromEntries(STATUSES.map(s => [s.value, s]));

function ApplicationModal({ app, onClose, onSave }) {
  const [form, setForm] = useState({
    company: app?.company || "",
    job_title: app?.job_title || "",
    status: app?.status || "saved",
    url: app?.url || "",
    notes: app?.notes || "",
    salary_range: app?.salary_range || "",
    location: app?.location || "",
    recruiter_email: app?.recruiter_email || "",
    date_saved: app?.date_saved || "",
    applied_date: app?.applied_date || "",
    platform: app?.platform || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.company.trim() || !form.job_title.trim()) {
      toast.error("Company and job title are required"); return;
    }
    setSaving(true);
    try {
      if (app?.id) {
        const res = await axios.patch(`${API}/applications/${app.id}`, form);
        onSave(res.data);
        toast.success("Application updated");
      } else {
        const res = await axios.post(`${API}/applications`, form);
        onSave(res.data);
        toast.success("Application created");
      }
      onClose();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="app-modal-overlay">
      <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="app-modal">
        <div className="modal-header">
          <h2>{app?.id ? "Edit Application" : "Add Application"}</h2>
          <button className="btn-icon" onClick={onClose} data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="modal-row">
            <div className="modal-field">
              <label>Company *</label>
              <input className="modal-input" placeholder="Google" value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })} data-testid="app-company-input" />
            </div>
            <div className="modal-field">
              <label>Job Title *</label>
              <input className="modal-input" placeholder="Senior Engineer" value={form.job_title}
                onChange={e => setForm({ ...form, job_title: e.target.value })} data-testid="app-title-input" />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Status</label>
              <select className="modal-input" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })} data-testid="app-status-select">
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="modal-field">
              <label>Location</label>
              <input className="modal-input" placeholder="San Francisco, CA" value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })} data-testid="app-location-input" />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Salary Range</label>
              <input className="modal-input" placeholder="$120k - $160k" value={form.salary_range}
                onChange={e => setForm({ ...form, salary_range: e.target.value })} data-testid="app-salary-input" />
            </div>
            <div className="modal-field">
              <label>Date Saved</label>
              <input className="modal-input" type="date" value={form.date_saved}
                onChange={e => setForm({ ...form, date_saved: e.target.value })} data-testid="app-date-saved-input" />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Date Applied</label>
              <input className="modal-input" type="date" value={form.applied_date}
                onChange={e => setForm({ ...form, applied_date: e.target.value })} data-testid="app-date-input" />
            </div>
            <div className="modal-field">
              <label>Recruiter Email</label>
              <input className="modal-input" placeholder="recruiter@company.com" value={form.recruiter_email}
                onChange={e => setForm({ ...form, recruiter_email: e.target.value })} data-testid="app-recruiter-email-input" />
            </div>
          </div>
          <div className="modal-field">
            <label>Job URL</label>
            <input className="modal-input" placeholder="https://..." value={form.url}
              onChange={e => setForm({ ...form, url: e.target.value })} data-testid="app-url-input" />
          </div>
          <div className="modal-field">
            <label>Notes</label>
            <textarea className="modal-textarea" placeholder="Add notes about this application..." value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="app-notes-input" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="dash-cta-btn" onClick={handleSubmit} disabled={saving} data-testid="app-save-btn">
            <Check size={14} /> {saving ? "Saving..." : app?.id ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function JobTrackerPage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [celebrate, setCelebrate] = useState({ open: false, prefill: {} });

  const openCelebrate = (app) => setCelebrate({
    open: true,
    prefill: { company: app?.company || "", job_title: app?.job_title || "", application_id: app?.id },
  });

  useEffect(() => { fetchApps(); }, []);

  const fetchApps = async () => {
    try {
      const res = await axios.get(`${API}/applications`);
      setApplications(res.data);
    } catch { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  };

  const handleSave = (app) => {
    setApplications(prev => {
      const idx = prev.findIndex(a => a.id === app.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = app; return n; }
      return [app, ...prev];
    });
    if (app.status === "offer_received" && editingApp?.status !== "offer_received") {
      openCelebrate(app);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/applications/${id}`);
      setApplications(prev => prev.filter(a => a.id !== id));
      toast.success("Application deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleStatusChange = async (id, newStatus) => {
    const prev = applications.find(a => a.id === id);
    try {
      const res = await axios.patch(`${API}/applications/${id}`, { status: newStatus });
      setApplications(prevApps => prevApps.map(a => a.id === id ? res.data : a));
      if (newStatus === "offer_received" && prev?.status !== "offer_received") {
        openCelebrate(res.data);
      }
    } catch { toast.error("Failed to update status"); }
  };

  const filtered = applications.filter(a => {
    if (filterStatus !== "all" && a.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return a.company.toLowerCase().includes(q) || a.job_title.toLowerCase().includes(q) || (a.location || "").toLowerCase().includes(q);
    }
    return true;
  });

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  const funnelStages = useMemo(() => [
    { key: "applied", label: "Applied", icon: "send", statuses: ["applied"] },
    { key: "shortlisted", label: "Shortlisted", icon: "star", statuses: ["shortlisted"] },
    { key: "interviewed", label: "Interviewed", icon: "mic", statuses: ["interviewed"] },
    { key: "offer", label: "Offers", icon: "trophy", statuses: ["offer_received"] },
  ], []);

  const funnelCounts = useMemo(() => {
    const c = {};
    funnelStages.forEach(s => {
      c[s.key] = applications.filter(a => s.statuses.includes(a.status)).length;
    });
    c.total = applications.length;
    c.rejected = applications.filter(a => a.status === "rejected").length;
    c.saved = applications.filter(a => a.status === "saved").length;
    c.rtr = applications.filter(a => a.status === "rtr_submitted").length;
    return c;
  }, [applications, funnelStages]);

  const conversionRate = (from, to) => {
    if (!from || from === 0) return 0;
    return Math.round((to / from) * 100);
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="job-tracker-page">
      <div className="page-header">
        <div>
          <h1>Job Tracker</h1>
          <p className="page-subtitle">Track your applications from submission to offer.</p>
        </div>
        <button className="dash-cta-btn" onClick={() => { setEditingApp(null); setShowModal(true); }} data-testid="add-application-btn">
          <Plus size={16} /> Add Application
        </button>
      </div>

      {/* Application Funnel */}
      {applications.length > 0 && (
        <div className="jt-funnel-container" data-testid="jt-funnel">
          <div className="jt-funnel-header">
            <h3 className="jt-funnel-title">Application Pipeline</h3>
            <span className="jt-funnel-total">{applications.length} total applications</span>
          </div>
          <div className="jt-funnel-stages">
            {funnelStages.map((stage, i) => {
              const count = funnelCounts[stage.key];
              const prevCount = i === 0 ? funnelCounts.total : funnelCounts[funnelStages[i - 1].key];
              const rate = conversionRate(prevCount, count);
              const widthPct = funnelCounts.total > 0 ? Math.max(20, (count / funnelCounts.total) * 100) : 20;
              return (
                <div key={stage.key} className="jt-funnel-stage-wrap">
                  {i > 0 && (
                    <div className="jt-funnel-arrow" data-testid={`funnel-arrow-${i}`}>
                      <ArrowRight size={14} />
                      <span className="jt-funnel-rate">{rate}%</span>
                    </div>
                  )}
                  <button
                    className={`jt-funnel-stage ${filterStatus === stage.statuses[0] ? "active" : ""} ${count > 0 ? "has-data" : ""}`}
                    onClick={() => setFilterStatus(filterStatus === stage.statuses[0] ? "all" : stage.statuses[0])}
                    data-testid={`funnel-${stage.key}`}
                  >
                    <div className="jt-funnel-bar" style={{ width: `${widthPct}%` }} />
                    <span className="jt-funnel-count">{count}</span>
                    <span className="jt-funnel-label">{stage.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="jt-funnel-extras">
            <button
              className={`jt-funnel-extra-pill ${filterStatus === "saved" ? "active" : ""}`}
              onClick={() => setFilterStatus(filterStatus === "saved" ? "all" : "saved")}
              data-testid="funnel-saved"
            >
              <span className="jt-pill-dot" style={{ background: "#64748b" }} /> Saved ({funnelCounts.saved})
            </button>
            <button
              className={`jt-funnel-extra-pill ${filterStatus === "rtr_submitted" ? "active" : ""}`}
              onClick={() => setFilterStatus(filterStatus === "rtr_submitted" ? "all" : "rtr_submitted")}
              data-testid="funnel-rtr"
            >
              <span className="jt-pill-dot" style={{ background: "#0ea5e9" }} /> RTR ({funnelCounts.rtr})
            </button>
            <button
              className={`jt-funnel-extra-pill ${filterStatus === "rejected" ? "active" : ""}`}
              onClick={() => setFilterStatus(filterStatus === "rejected" ? "all" : "rejected")}
              data-testid="funnel-rejected"
            >
              <span className="jt-pill-dot" style={{ background: "#ef4444" }} /> Rejected ({funnelCounts.rejected})
            </button>
            {filterStatus !== "all" && (
              <button className="jt-funnel-extra-pill clear" onClick={() => setFilterStatus("all")} data-testid="funnel-clear-filter">
                <X size={12} /> Clear Filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="jt-search-wrap" data-testid="jt-search">
        <Search size={16} />
        <input className="jt-search-input" placeholder="Search by company, title, or location..."
          value={search} onChange={e => setSearch(e.target.value)} data-testid="jt-search-input" />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="dash-empty-card" data-testid="jt-empty">
          <div className="dash-empty-icon"><Briefcase size={28} /></div>
          <h3>{applications.length === 0 ? "No applications yet" : "No matches"}</h3>
          <p>{applications.length === 0 ? "Start tracking your job applications. Optimized resumes are automatically tracked!" : "Try a different filter or search."}</p>
          {applications.length === 0 && (
            <button className="dash-cta-btn" onClick={() => { setEditingApp(null); setShowModal(true); }} data-testid="jt-empty-add-btn">
              <Plus size={16} /> Add Your First Application
            </button>
          )}
        </div>
      ) : (
        <div className="jt-table-wrap" data-testid="jt-table">
          <table className="jt-table">
            <thead>
              <tr>
                <th>Company & Role</th>
                <th>Status</th>
                <th>Location</th>
                <th>Date</th>
                <th>Recruiter</th>
                <th>Resume</th>
                <th style={{ width: "120px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(app => {
                const st = statusMap[app.status] || statusMap.saved;
                return (
                  <tr key={app.id} data-testid={`jt-row-${app.id}`}>
                    <td>
                      <div className="jt-cell-company">
                        <div className="jt-cell-title" data-testid={`jt-title-${app.id}`}>{app.job_title}</div>
                        <div className="jt-cell-company-name">
                          {app.company}
                          {app.url && (
                            <a href={app.url} target="_blank" rel="noopener noreferrer" className="jt-cell-link" data-testid={`jt-url-${app.id}`}>
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <select className="jt-status-pill" value={app.status}
                        onChange={e => handleStatusChange(app.id, e.target.value)}
                        style={{ color: st.color, background: st.bg }}
                        data-testid={`jt-status-${app.id}`}
                      >
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <span className="jt-cell-location">
                        {app.location ? <><MapPin size={11} /> {app.location}</> : <span className="jt-cell-muted">-</span>}
                      </span>
                    </td>
                    <td>
                      <span className="jt-cell-date">{formatDate(app.applied_date)}</span>
                    </td>
                    <td>
                      {app.recruiter_email ? (
                        <a href={`mailto:${app.recruiter_email}`} className="jt-recruiter-link" data-testid={`jt-recruiter-${app.id}`}>
                          <Mail size={11} /> {app.recruiter_email.length > 20 ? app.recruiter_email.slice(0, 20) + "..." : app.recruiter_email}
                        </a>
                      ) : (
                        <span className="jt-cell-muted">-</span>
                      )}
                    </td>
                    <td>
                      {app.optimization_id ? (
                        <span className="jt-resume-link" data-testid={`jt-resume-${app.id}`}><FileText size={11} /> Linked</span>
                      ) : (
                        <span className="jt-cell-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div className="jt-cell-actions">
                        {app.optimization_id && (
                          <button className="btn-icon" onClick={() => navigate(`/app/tracker/${app.id}`)} title="View Details" data-testid={`jt-view-${app.id}`}>
                            <Eye size={14} />
                          </button>
                        )}
                        <button className="btn-icon" onClick={() => { setEditingApp(app); setShowModal(true); }} title="Edit" data-testid={`jt-edit-${app.id}`}>
                          <Edit3 size={14} />
                        </button>
                        <button className="btn-icon danger" onClick={() => handleDelete(app.id)} title="Delete" data-testid={`jt-delete-${app.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ApplicationModal
          app={editingApp}
          onClose={() => { setShowModal(false); setEditingApp(null); }}
          onSave={handleSave}
        />
      )}

      <CelebrateModal
        open={celebrate.open}
        prefill={celebrate.prefill}
        onClose={() => setCelebrate({ open: false, prefill: {} })}
      />
    </div>
  );
}
